import React, { Fragment, useState, useEffect } from 'react';
import styled, { withTheme, css } from 'styled-components';
import { compose } from 'recompose';
import { withTranslation } from 'react-i18next';
import { withAnalytic } from '@skooldio/analytics-provider';

import { useConfig } from 'Context/ConfigContext';
import { withOffset } from 'Context/LayoutContext';
import {
  genTOCEvent,
  TOC_ACTIONS,
  sendDataToGTM,
  GTM_EVENT_NAME,
  GTM_DATA_LAYER_PROPERTY,
  GTM_BUTTON_LOCATION_NAME,
} from 'Util/Analytics';
import {
  Flex,
  Box,
  Image,
  Text,
  SvgIcon,
  ButtonFilled,
  ProgressBar as BaseProgressBar,
} from 'Components/Base';
import { get as getTheme } from 'Components/Base/constants';
import PageTitle from 'Components/PageTitle';
import CourseToC from 'Components/PaaS/Classroom/CourseToC/CourseToC';
import Notification from 'Components/Notification';
import { PreviewModal, PREVIEW_MESSAGE_TYPE } from 'Containers/Preview';
import { useClassroomInterruption, useClassroomNavigation, useDisclosure } from 'Util/Hooks';
import { useMyCourseData, useOnlineCoursesData } from 'Domain/MyCourseData';
import { Footer } from 'Containers/Classroom/FixedView';
import { useGoToPurchase } from 'Domain/ExternalNavigation';
import ExitModal from 'Containers/Exit';

import { ClassroomLayoutView } from '../Components/ClassroomLayout';
import {
  ClassroomNavigation,
  ClassroomSubsection,
  useClassroomCourseData,
} from '../../../Domain/Classroom';
import { SubsectionContent } from '../Components';
import CourseReviewButton from '../Container/CourseReviewButton';
import ExitPreviewClassroomButton from '../Container/ExitPreviewClassroomButton';
import DiscussionModule from '../Container/DiscussionModule';

const Title = (props) => <Text px={7} fontFamily={'content'} {...props} />;

const HeaderWrapper = styled(Flex)`
  ${({ theme }) => css`
    position: fixed;
    top: 0;
    width: 100%;
    padding-top: ${theme.space.APP_TOP_OFFSET}px;
    height: ${theme.space.CLASSROOM_NAVBAR_HEIGHT + theme.space.APP_TOP_OFFSET}px;
    border-top: 4px solid ${theme.colors.primary};
    box-shadow: 0px 2px 2px rgba(232, 232, 232, 0.5);
    z-index: 5;

    @media (min-width: ${theme.breakpoints[0]}) {
      height: ${theme.space.CLASSROOM_NAVBAR_HEIGHT}px;
      padding-top: 0px;
    }
  `}
`;

// FIXME: SKOOLDIO-5053 Remove APP_TOP_OFFSET param and use `safe area insets` instead
const TitleInformation = styled(Flex)`
  ${({ isMobile, theme: { space = {}, colors = {} } }) => css`
    flex: 0 0 auto;
    padding-top: ${isMobile ? `${space.APP_TOP_OFFSET}px` : 'auto'};
    height: ${isMobile ? `${space.APP_TOP_OFFSET + space.CLASSROOM_NAVBAR_HEIGHT}px` : 'auto'};
  `}
`;

const SidebarWrapper = styled(Flex)`
  ${({ isMobile, isMinimize, disableNavbar, theme: { space = {} } }) => css`
    width: ${isMinimize ? '56px' : '360px'};
    transition: width 0.3s ease-in-out;
    position: sticky;
    top: ${space.CLASSROOM_NAVBAR_HEIGHT}px;
    height: ${disableNavbar ? '100vh' : `calc(100vh - ${space.CLASSROOM_NAVBAR_HEIGHT}px)`};
    display: ${isMobile ? 'none' : 'flex'};
    overflow: hidden;
    /* PaaS style */
    box-shadow: 0px -2px 2px rgba(232, 232, 232, 0.5);
    && > * {
      min-width: ${isMinimize ? '56px' : '360px'};
    }
  `}
`;

const IconWrapper = styled((props) => (
  <Flex
    justifyContent={'center'}
    alignItems={'center'}
    width={'32px'}
    height={'32px'}
    bg={'sidebarToggleButton'}
    p={1}
    {...props}
  />
))`
  ${({ theme: { breakpoints = [], colors = {} } }) => css`
    && {
      border-radius: 100%;
      min-width: 32px;
      position: absolute;
      right: 0;
      top: 16px;
      transform: translateX(50%);
      cursor: pointer;
      display: none;
      z-index: 5;
      box-shadow: 0px 4px 4px rgba(0, 0, 0, 0.05);

      @media (min-width: ${breakpoints[1]}) {
        display: flex;
      }
    }
  `}
`;

const ToggleIcon = styled((props) => (
  <SvgIcon icon={SvgIcon.ICON_LIST.BACK} size={28} {...props} />
))`
  ${({ isMinimize, theme }) => css`
    transform: ${isMinimize ? 'rotate(180deg)' : 'rotate(0)'};
    transition: all 0.3s;
    color: ${theme.colors?.sidebarToggleIcon ?? 'white'};
  `}
`;

const SidebarFooterWrapper = styled(Box)`
  box-shadow: 0px 1px 0px rgba(17, 17, 26, 0.05), 0px 0px 8px rgba(17, 17, 26, 0.1);
`;

const ToCWrapper = styled(Box)`
  overflow: auto;
`;

const CourseContentWrapper = styled.article`
  ${({ hideSubsectionTitle, backgroundColor, theme: { space, breakpoints } }) =>
    css`
      padding-top: ${hideSubsectionTitle ? `${space[7]}px` : 'unset'};
      background-color: ${backgroundColor ? backgroundColor : 'inherit'};
      display: block;
      min-height: ${({ theme }) =>
        `calc(100vh - ${theme.space.CLASSROOM_NAVBAR_HEIGHT}px - ${theme.space.CLASSROOM_FOOTER_HEIGHT}px)`};
      overflow-y: auto;
      padding-bottom: 24px;
      /*  Workaround YouTube fullscreen video js on Safari*/
      -webkit-overflow-scrolling: auto;
    `}
`;

const LogoAdornment = styled(Image)`
  ${({ theme }) => css`
    border-left: 1px solid black;
    padding-left: 12px;
    margin-left: 12px;
    max-width: 65px;
    max-height: ${theme.space.CLASSROOM_NAVBAR_HEIGHT}px;
  `}
`;

const ForumIcon = styled(SvgIcon)`
  ${({ theme: { colors = {} } }) => css`
    width: 24px;
    height: 24px;

    .forum-icon {
      fill: ${colors.primary};
    }
  `}
`;

const ProgressBar = styled(BaseProgressBar)`
  border-radius: 20px;
  overflow: hidden;
  * {
    margin-top: 0px;
  }
`;

const AchieveIconWrapper = styled((props) => (
  <Flex bg={'primaryLighter'} flex={'0 0 auto'} {...props} />
))`
  padding: 6px;
  border-radius: 4px;

  svg > * {
    fill: ${getTheme('colors.primary')};
  }
`;

const PurchaseButton = styled(ButtonFilled)`
  padding: 4px 16px;
  height: auto;
  min-width: auto;
`;

export const SidebarContent = ({
  sidebarContentPadding,
  handleGoToDiscussion,
  onExitClassroom,
  theme: { colors = {} },
  translation,
  isMinimize,
  toggleMiniMode = () => {},
  isMobile,
  onModalToggle = () => {},
}) => {
  const { enableDiscussion, enableSkooldioCourseReview, classroom } = useConfig();
  const { data: myCourseData, courseCode, discussUrl } = useMyCourseData();
  const { data: onlineCourseData } = useOnlineCoursesData({ courseCode: courseCode });
  const { data: classroomCourseData, isClassroomTrialMode } = useClassroomCourseData();
  const { goToSubsection, currentSection, currentSubsection } = useClassroomNavigation();
  const { enablePurchaseUrl, purchaseUrl } = useGoToPurchase(onlineCourseData?.SKUCode, {
    utmMedium: 'Purchase',
    courseTitle: myCourseData.courseInfo?.title,
    sectionTitle: currentSection.title,
    subsectionTitle: currentSubsection.title,
  });
  const title = classroomCourseData?.classroomCourse?.title;
  const {
    discussion,
    disableToCBackToMyCourse,
    disableToCBackToCourseDetail,
    enableToggleMiniModeOnSubsectionClick,
  } = classroom ?? {};
  const progressPercent = myCourseData?.courseInfo?.classroomProgress?.progressPercent;
  const floorProgressPercent = Math.floor(progressPercent ?? 0);

  const handleOnPurchaseClick = () => {
    window.location = purchaseUrl;

    // logging GTM event
    sendDataToGTM({
      event: GTM_EVENT_NAME.TRIAL_PURCHASE_CLICK,
      [GTM_DATA_LAYER_PROPERTY.PRODUCT_TITLE]: title,
      [GTM_DATA_LAYER_PROPERTY.BUTTON_LOCATION]: GTM_BUTTON_LOCATION_NAME.HEADER,
    });
  };

  return (
    <Fragment>
      {(!isMinimize || isMobile) && (
        <Fragment>
          {title && (
            <Box py={4} style={{ borderBottom: `1px solid ${colors.sidebarPrimaryDivider}` }}>
              <TitleInformation width={1} isMobile={isMobile} bg={'white'}>
                <Flex alignItems={'center'} width={1}>
                  <Title width={1} fontWeight="bold" fontSize={2} lineHeight="1.35" mb={2}>
                    {title}
                  </Title>
                </Flex>
              </TitleInformation>

              {isClassroomTrialMode && enablePurchaseUrl && (
                <Flex alignItems={'center'} px={7}>
                  <PurchaseButton
                    color={'sidebarPurchaseCourseButton'}
                    variant={'small'}
                    onClick={handleOnPurchaseClick}
                  >
                    {translation('BUTTON.PURCHASE_COURSE')}
                  </PurchaseButton>
                </Flex>
              )}

              {!isClassroomTrialMode && (
                <Fragment>
                  <Flex px={7} width={1} alignItems={'center'} justifyContent={'space-between'}>
                    <ProgressBar value={floorProgressPercent} withLabel={false} width={1} />
                    <AchieveIconWrapper ml={3}>
                      <SvgIcon
                        icon={SvgIcon.ICON_LIST.ACHIEVE}
                        size={24}
                        style={{ flex: '0 0 auto' }}
                      />
                    </AchieveIconWrapper>
                  </Flex>
                  <Text textStyle={'caption'} px={7} fontWeight={'normal'}>
                    {translation('PROGRESS_COMPLETE', { percent: floorProgressPercent })}
                  </Text>
                </Fragment>
              )}
            </Box>
          )}

          <ToCWrapper>
            <ClassroomNavigation>
              {({ sections }) => (
                <CourseToC
                  px={sidebarContentPadding}
                  py={4}
                  sections={sections}
                  onSubsectionClick={(...args) => {
                    // Close TOC modal on subsection click
                    onModalToggle();

                    // toggle sidebar on subsection click
                    // Sidebar will toggle only for skooldio app
                    if (enableToggleMiniModeOnSubsectionClick) toggleMiniMode();

                    goToSubsection(...args);

                    // logging GTM event
                    sendDataToGTM({
                      event: GTM_EVENT_NAME.SUBSECTION_CLICK,
                      [GTM_DATA_LAYER_PROPERTY.PRODUCT_TITLE]: title,
                      [GTM_DATA_LAYER_PROPERTY.SUBSECTION_TITLE]: args?.[2],
                    });
                  }}
                />
              )}
            </ClassroomNavigation>
          </ToCWrapper>
        </Fragment>
      )}

      <SidebarFooterWrapper bg="sidebarPrimaryBackground" mt="auto">
        {enableSkooldioCourseReview && (
          <CourseReviewButton
            sidebarContentPadding={sidebarContentPadding}
            isMinimize={isMinimize}
            isMobile={isMobile}
          />
        )}
        {discussion?.enable && <DiscussionModule isMinimize={isMinimize} />}
        {enableDiscussion && !isClassroomTrialMode && discussUrl && (
          <ButtonFilled
            px={sidebarContentPadding}
            py={4}
            onClick={() => handleGoToDiscussion(discussUrl, title)}
            color={'white'}
            border={0}
            width="100%"
          >
            <Flex alignItems="center">
              <Text lineHeight={0} mr={5}>
                <ForumIcon icon={SvgIcon.ICON_LIST.FORUM} />
              </Text>
              {(!isMinimize || isMobile) && (
                <Text fontWeight="medium" fontFamily="content" color={'text'}>
                  {translation('DISCUSSION')}
                </Text>
              )}
            </Flex>
          </ButtonFilled>
        )}
        {!disableToCBackToMyCourse && (
          <>
            {isClassroomTrialMode && !disableToCBackToCourseDetail ? (
              <ExitPreviewClassroomButton
                px={sidebarContentPadding}
                isHideText={isMinimize && !isMobile}
              />
            ) : (
              <ButtonFilled
                px={sidebarContentPadding}
                py={4}
                onClick={onExitClassroom}
                color={'white'}
                border={0}
                width="100%"
              >
                <Flex alignItems="center">
                  <Text lineHeight={0} mr={5} color="primary">
                    <SvgIcon size={24} icon={SvgIcon.ICON_LIST.EXIT} />
                  </Text>
                  {(!isMinimize || isMobile) && (
                    <Text fontWeight="medium" fontFamily="content" color={'text'}>
                      {translation('EXIT_CLASSROOM')}
                    </Text>
                  )}
                </Flex>
              </ButtonFilled>
            )}
          </>
        )}
      </SidebarFooterWrapper>
    </Fragment>
  );
};

const Header = ({
  courseOwnerLogoUrl,
  partnerLogoUrl,
  sidebarContentPadding,
  onExitClassroom,
  theme,
  isMobile,
}) => {
  const { isClassroomTrialMode } = useClassroomCourseData();
  const { colors } = theme;
  return (
    <HeaderWrapper
      pr={isMobile ? 4 : 7}
      pl={sidebarContentPadding}
      bg="navBarBackground"
      justifyContent="space-between"
      alignItems="center"
    >
      <Flex justifyContent="space-between" alignItems="center">
        <Box
          as="a"
          onClick={onExitClassroom}
          style={{
            textDecoration: 'none',
            cursor: 'pointer',
            display: 'flex',
            flex: '0 1 auto',
            alignItems: 'center',
          }}
        >
          <Image src={courseOwnerLogoUrl} style={{ maxWidth: 120, maxHeight: 45 }} />
          {partnerLogoUrl && <LogoAdornment src={partnerLogoUrl} />}
        </Box>
      </Flex>
      {!isClassroomTrialMode && (
        <Notification
          height={'100%'}
          iconProps={{
            bg: 'transparent',
            color: colors.notificationIcon,
            outlined: true,
          }}
        />
      )}
    </HeaderWrapper>
  );
};

const Sidebar = ({
  handleGoToDiscussion,
  onExitClassroom,
  sidebarContentPadding,
  theme,
  isMobile,
  isMinimize,
  toggleMiniMode,
  translation,
}) => {
  const {
    global: { disableNavbar },
  } = useConfig();

  return (
    <Box style={{ position: 'relative' }}>
      <SidebarWrapper
        id="sidebar"
        flexDirection="column"
        bg="sidebarPrimaryBackground"
        color="sidebarPrimaryText"
        isMinimize={isMinimize}
        isMobile={isMobile}
        disableNavbar={disableNavbar}
      >
        <SidebarContent
          sidebarContentPadding={sidebarContentPadding}
          handleGoToDiscussion={handleGoToDiscussion}
          onExitClassroom={onExitClassroom}
          theme={theme}
          isMinimize={isMinimize}
          toggleMiniMode={toggleMiniMode}
          isMobile={isMobile}
          translation={translation}
        />
      </SidebarWrapper>
      <IconWrapper onClick={toggleMiniMode}>
        <ToggleIcon isMinimize={isMinimize} />
      </IconWrapper>
    </Box>
  );
};

const Main = ({ contentBackgroundColor, handleOpenExitModal, allowContentType, isMobile }) => {
  const {
    classroom: { hideSubsectionTitle },
  } = useConfig();
  const { courseCode } = useMyCourseData();
  const { currentSubsection, updateFlutterHeader } = useClassroomNavigation();

  useEffect(() => {
    updateFlutterHeader(courseCode);
  }, [courseCode, currentSubsection, updateFlutterHeader]);

  return (
    <>
      <CourseContentWrapper
        backgroundColor={contentBackgroundColor}
        hideSubsectionTitle={hideSubsectionTitle}
      >
        {!hideSubsectionTitle && currentSubsection && (
          <Title fontWeight="medium" fontSize={4} lineHeight="title" textAlign="center" py={'14px'}>
            {currentSubsection.title}
          </Title>
        )}
        <ClassroomSubsection>
          {({ description }) =>
            description ? (
              <Flex px={7} justifyContent={'center'}>
                <Text fontFamily={'contentReadable'}>{description}</Text>
              </Flex>
            ) : null
          }
        </ClassroomSubsection>

        <SubsectionContent
          onExitClassroom={handleOpenExitModal}
          allowedTypes={[...allowContentType]}
        />
      </CourseContentWrapper>
      {!isMobile && <Footer isMobile={isMobile} />}
    </>
  );
};

const ClassroomFixedView = ({
  onLogAction,
  courseOwnerLogoUrl,
  partnerLogoUrl,
  allowContentType,
  contentBackgroundColor,
  handleExitClassroom: onExitClassroom,
  handleGoToDiscussion,
  isMobile,
  theme,
  t,
}) => {
  const [isExitModalOpened, onOpenExitModal, onCloseExitModal] = useDisclosure();
  const [isMinimize, setIsMinimize] = useState(false);

  useEffect(() => {
    const syncMinimizeState = (event) => {
      if (event.data.type !== 'DISCUSSION_STATE_CHANGE') return;
      if (event.data.payload !== 'ACTIVE') return;
      setIsMinimize(false);
    };
    window.addEventListener('message', syncMinimizeState);
    return () => window.removeEventListener('message', syncMinimizeState);
  }, []);

  const {
    global: { disableNavbar },
    classroom: { enableExitModal },
    global: { pageSuffix = '' },
  } = useConfig();
  const { data: classroomCourseData, isClassroomTrialMode } = useClassroomCourseData();
  const { isInterrupting, activateInterruption, deactivateInterruption } =
    useClassroomInterruption();

  const title = classroomCourseData?.classroomCourse?.title;
  const analyticLogger = (isOpen) => {
    const tocClickAction = isOpen ? TOC_ACTIONS.OPEN : TOC_ACTIONS.CLOSE;
    onLogAction(genTOCEvent(tocClickAction, 'TOC Button'));
  };

  const sidebarContentPadding = isMinimize || isMobile ? 4 : 7;

  const toggleMiniMode = (e, bool = !isMinimize) => {
    setIsMinimize(bool);
    analyticLogger(isMinimize);
  };

  useEffect(() => {
    if (isClassroomTrialMode) {
      activateInterruption();
    }
  }, []);

  return (
    <Fragment>
      {title && <PageTitle title={`${title ? `${title} ` : ''}${pageSuffix}`} />}
      <ClassroomLayoutView
        variant={ClassroomLayoutView.VARIANTS.FIXED}
        renderHeader={() =>
          !disableNavbar ? (
            <Header
              courseOwnerLogoUrl={courseOwnerLogoUrl}
              partnerLogoUrl={partnerLogoUrl}
              sidebarContentPadding={sidebarContentPadding}
              handleGoToDiscussion={handleGoToDiscussion}
              onExitClassroom={enableExitModal ? onOpenExitModal : onExitClassroom}
              translation={t}
              isMobile={isMobile}
              isMinimize={isMinimize}
              theme={theme}
              analyticLogger={analyticLogger}
            />
          ) : null
        }
        renderSidebar={() =>
          !isMobile ? (
            <Sidebar
              handleGoToDiscussion={handleGoToDiscussion}
              onExitClassroom={enableExitModal ? onOpenExitModal : onExitClassroom}
              sidebarContentPadding={sidebarContentPadding}
              theme={theme}
              isMobile={isMobile}
              isMinimize={isMinimize}
              toggleMiniMode={toggleMiniMode}
              translation={t}
            />
          ) : null
        }
        renderChildren={() => (
          <Main
            contentBackgroundColor={contentBackgroundColor}
            handleOpenExitModal={enableExitModal ? onOpenExitModal : onExitClassroom}
            allowContentType={allowContentType}
            isMobile={isMobile}
          />
        )}
        childrenProps={{ id: 'course-content-wrapper' }}
        renderFooter={() =>
          isMobile ? (
            <Footer
              onLogAction={onLogAction}
              handleOpenExitModal={enableExitModal ? onOpenExitModal : onExitClassroom}
              isMobile={isMobile}
            />
          ) : null
        }
        footerProps={{ pb: isMobile ? `${theme.space.CLASSROOM_FOOTER_HEIGHT}px` : 'unset' }}
        contentProps={{
          style: {
            height: '100%',
            minHeight: `calc(100vh - ${theme.space.CLASSROOM_NAVBAR_HEIGHT}px - ${theme.space.CLASSROOM_FOOTER_HEIGHT}px)`,
          },
        }}
      />

      {isClassroomTrialMode && (
        <PreviewModal
          previewModalType={PREVIEW_MESSAGE_TYPE.ACKNOWLEDGE}
          isInterrupting={isInterrupting}
          deactivateInterruption={deactivateInterruption}
        />
      )}
      {enableExitModal && (
        <ExitModal
          openExitModal={isExitModalOpened}
          onExitClassroom={onExitClassroom}
          onCloseExitModal={onCloseExitModal}
        />
      )}
    </Fragment>
  );
};

export default compose(
  withTheme,
  withTranslation('classroom'),
  withAnalytic,
  withOffset
)(ClassroomFixedView);
